import { useParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  PurchaseOrderDetail,
  PurchaseOrderLandedCostsResponse,
} from '../../lib/types/procurement.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/**
 * Official Purchase Order printable document (سفارش رسمی خرید کالا و خدمات).
 * Conforms to standard Iranian corporate procurement procedures:
 * - Purchaser legal entity header (کد اقتصادی، شناسه ملی، آدرس، تلفن)
 * - Document metadata (شماره سفارش، تاریخ صدور، تاریخ تحویل، پیش‌نیازها)
 * - Supplier / Vendor corporate profile
 * - Itemized specifications table (کد کالا، شرح، تعداد، واحد، فی و مبلغ کل)
 * - Landed costs & financial summary (هزینه‌های سربار و بهای نهایی)
 * - Commercial terms & conditions (شرایط تحویل و تسویه حساب)
 * - 4-step formal authorized signatures box (تدارکات، مالی، مدیریت، فروشنده).
 */
export function PurchaseOrderPrintPage() {
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const { unit, label, ready } = useAmountDisplay();
  const base = slug ? tenantBasePath(slug) : '';

  const order = useApiGet<PurchaseOrderDetail>(
    slug && ref ? `${base}/api/v1/procurement/orders/${ref}` : null,
    [slug, ref],
  );
  const landedCosts = useApiGet<PurchaseOrderLandedCostsResponse>(
    slug && ref ? `${base}/api/v1/procurement/orders/${ref}/landed-costs` : null,
    [slug, ref],
  );
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?limit=200` : null,
    [slug],
  );

  if (!slug || !ref) return null;
  const backHref = `${base}/procurement/orders/${ref}`;

  if (!ready) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سفارش خرید">
        <PrintLoadState />
      </PrintPageShell>
    );
  }

  if (!isReady(order)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سفارش خرید">
        <PrintLoadState error={order.status === 'error' ? order.error : undefined} />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;

  const partyRow =
    isReady(parties) && order.data.supplierPartyRef
      ? parties.data.items.find((p) => String(p.code) === order.data.supplierPartyRef)
      : undefined;

  const supplierName = partyRow
    ? String(partyRow.name ?? partyRow.code)
    : (order.data.supplierPartyRef ?? '—');

  const lines = order.data.lines ?? [];
  const today = systemClock.today();
  const landedData = isReady(landedCosts) ? landedCosts.data : null;

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به سفارش خرید">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 12 }}>
        {/* Top Header: Purchaser identity (Right) and PO Metadata (Left) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>
              سفارش رسمی خرید کالا و خدمات
            </span>
            <span
              className="ltr sk-num"
              style={{ fontFamily: 'var(--sk-font-mono)', fontWeight: 600 }}
            >
              شماره سفارش: {ref}
            </span>
            {order.data.requestRef ? (
              <span
                className="ltr sk-num"
                style={{ fontFamily: 'var(--sk-font-mono)', color: '#555' }}
              >
                عطف به درخواست: {order.data.requestRef}
              </span>
            ) : null}
            <span>تاریخ صدور: {displayJalaliDate(order.data.orderDate || today)}</span>
            <span>موعد تحویل: {displayJalaliDate(order.data.expectedDate)}</span>
            <span style={{ fontSize: 11, color: '#444' }}>
              وضعیت: <b>{order.data.statusLabel}</b>
            </span>
          </div>
        </div>

        {/* Vendor & Logistics Box */}
        <div
          style={{
            border: '1px solid #bbb',
            borderRadius: 6,
            padding: '10px 14px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
            backgroundColor: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 700, color: '#333' }}>مشخصات فروشنده / تأمین‌کننده:</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <span>
                نام: <b>{supplierName}</b>
              </span>
              <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                کد طرف‌حساب: {order.data.supplierPartyRef}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 700, color: '#333' }}>مشخصات تحویل و حمل:</span>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {order.data.carrierName ? (
                <span>
                  متصدی حمل: <b>{order.data.carrierName}</b>
                </span>
              ) : null}
              {order.data.trackingNumber ? (
                <span className="ltr sk-num">
                  شماره بارنامه/رهگیری: <b>{order.data.trackingNumber}</b>
                </span>
              ) : null}
              {order.data.shippedAt ? (
                <span>تاریخ ارسال: {displayJalaliDate(order.data.shippedAt)}</span>
              ) : null}
              {!order.data.carrierName && !order.data.trackingNumber ? (
                <span style={{ color: '#777' }}>تحویل در انبار مرکزی شرکت</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Order Items Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #222', backgroundColor: '#f2f2f2' }}>
              <th style={{ textAlign: 'right', padding: '7px 6px', width: 36 }}>#</th>
              <th style={{ textAlign: 'right', padding: '7px 6px' }}>شرح کالا یا خدمات</th>
              <th style={{ textAlign: 'right', padding: '7px 6px', width: 110 }}>کد کالا</th>
              <th style={{ textAlign: 'center', padding: '7px 6px', width: 70 }}>مقدار</th>
              <th style={{ textAlign: 'center', padding: '7px 6px', width: 65 }}>واحد</th>
              <th style={{ textAlign: 'left', padding: '7px 6px', width: 120 }}>فی ({label})</th>
              <th style={{ textAlign: 'left', padding: '7px 6px', width: 130 }}>
                جمع ردیف ({label})
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '16px 4px', textAlign: 'center', color: '#777' }}>
                  هیچ قلم کالایی برای این سفارش ثبت نشده است.
                </td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.rowNumber} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '7px 6px' }}>{line.rowNumber.toLocaleString('fa-IR')}</td>
                  <td style={{ padding: '7px 6px' }}>
                    <div style={{ fontWeight: 600 }}>{line.description || line.productRef}</div>
                    {line.notes ? (
                      <div style={{ fontSize: 10.5, color: '#666' }}>{line.notes}</div>
                    ) : null}
                  </td>
                  <td
                    className="ltr sk-num"
                    style={{ padding: '7px 6px', fontFamily: 'var(--sk-font-mono)' }}
                  >
                    {line.productRef}
                  </td>
                  <td className="sk-num" style={{ padding: '7px 6px', textAlign: 'center' }}>
                    {line.quantity}
                  </td>
                  <td style={{ padding: '7px 6px', textAlign: 'center' }}>{line.uomRef}</td>
                  <td className="ltr sk-num" style={{ padding: '7px 6px', textAlign: 'left' }}>
                    {formatRialFa(line.unitPriceRial, unit)}
                  </td>
                  <td
                    className="ltr sk-num"
                    style={{ padding: '7px 6px', textAlign: 'left', fontWeight: 600 }}
                  >
                    {formatRialFa(line.lineTotalRial, unit)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Financial Totals & Landed Costs */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <div
            style={{
              width: 320,
              border: '1px solid #bbb',
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              backgroundColor: '#fafafa',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
              <span>جمع بهای اقلام:</span>
              <span className="ltr sk-num">
                {formatRialFa(order.data.subtotalRial || order.data.totalRial, unit)} {label}
              </span>
            </div>

            {landedData && Number(landedData.totalChargesRial) > 0 ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: '#555',
                  }}
                >
                  <span>هزینه‌های سربار و ترخیص:</span>
                  <span className="ltr sk-num">
                    +{formatRialFa(landedData.totalChargesRial, unit)} {label}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 700,
                    borderTop: '1px dashed #ccc',
                    paddingTop: 4,
                  }}
                >
                  <span>بهای تمام‌شده کل:</span>
                  <span className="ltr sk-num" style={{ color: '#0d5c2f' }}>
                    {formatRialFa(landedData.grandTotalRial, unit)} {label}
                  </span>
                </div>
              </>
            ) : null}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 700,
                fontSize: 13,
                borderTop: '1px solid #222',
                paddingTop: 6,
              }}
            >
              <span>مبلغ کل سفارش:</span>
              <span className="ltr sk-num">
                {formatRialFa(order.data.totalRial, unit)} {label}
              </span>
            </div>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 11,
            color: '#444',
            lineHeight: 1.8,
            backgroundColor: '#fff',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>شرایط و الزامات قانونی سفارش خرید:</div>
          <div>
            ۱. تحویل اقلام باید کاملاً منطبق با مشخصات فنی، برند، بسته‌بندی استاندارد و در موعد مقرر
            انجام پذیرد.
          </div>
          <div>
            ۲. تسویه حساب مالی منوط به بازرسی کیفی، تحویل کامل به انبار مقصد، صدور رسید رسمی انبار و
            ارائه فاکتور رسمی معتبر سامانه مودیان خواهد بود.
          </div>
          <div>
            ۳. هرگونه مغایرت فنی یا تأخیر غیرموجه در تحویل، موجب اعمال جریمه تأخیر یا فسخ سفارش طبق
            آیین‌نامه معاملات خواهد شد.
          </div>
        </div>

        {/* 4-Column Authorized Signatures & Approval Blocks */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
            marginTop: 14,
          }}
        >
          <div
            style={{
              border: '1px solid #aaa',
              borderRadius: 6,
              padding: '10px 8px',
              minHeight: 85,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 11 }}>کارشناس تدارکات و خرید</span>
            <span style={{ fontSize: 10, color: '#666' }}>تنظیم‌کننده سفارش</span>
            <div style={{ height: 26 }}></div>
            <span style={{ fontSize: 10, color: '#888' }}>امضا و تاریخ</span>
          </div>

          <div
            style={{
              border: '1px solid #aaa',
              borderRadius: 6,
              padding: '10px 8px',
              minHeight: 85,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 11 }}>مدیر امور مالی و بودجه</span>
            <span style={{ fontSize: 10, color: '#666' }}>تأیید اعتبار و بودجه</span>
            <div style={{ height: 26 }}></div>
            <span style={{ fontSize: 10, color: '#888' }}>امضا و تاریخ</span>
          </div>

          <div
            style={{
              border: '1px solid #aaa',
              borderRadius: 6,
              padding: '10px 8px',
              minHeight: 85,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 11 }}>مدیرعامل / مقام مجاز</span>
            <span style={{ fontSize: 10, color: '#666' }}>تصویب نهایی</span>
            <div style={{ height: 26 }}></div>
            <span style={{ fontSize: 10, color: '#888' }}>امضا و مهر رسمی</span>
          </div>

          <div
            style={{
              border: '1px solid #aaa',
              borderRadius: 6,
              padding: '10px 8px',
              minHeight: 85,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 11 }}>تأمین‌کننده / فروشنده</span>
            <span style={{ fontSize: 10, color: '#666' }}>تأیید و تعهد تحویل</span>
            <div style={{ height: 26 }}></div>
            <span style={{ fontSize: 10, color: '#888' }}>امضا و مهر فروشنده</span>
          </div>
        </div>
      </div>
    </PrintPageShell>
  );
}
